import { DashboardFilters } from "@/components/dashboard/dashboard-filters";
import { DashboardDeregistrationQuestions } from "@/components/non_registrant/dashboard-deregistration-questions";
import { DashboardInspectionQuestions } from "@/components/non_registrant/dashboard-inspection-questions";
import { DashboardQuestions } from "@/components/non_registrant/dashboard-questions";
import PageHeader from "@/components/page-header";
import {
  SETUP_PAGE_CHROME_CLASS,
  SETUP_PAGE_LIST_TAB_CLASS,
  SETUP_PAGE_SHELL_CLASS,
  SETUP_PAGE_TABS_CLASS,
} from "@/components/portal/setup-page-layout";
import {
  SETUP_TABS_LIST_CLASS,
  SETUP_TAB_TRIGGER_CLASS,
  SetupPageTabBar,
} from "@/components/portal/setup-page-tab-bar";
import LoadingScreen from "@/components/ui/loading-screen";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserType } from "@/constants";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useGovernmentDashboardStats } from "@/hooks/use-government-dashboard-stats";
import { useGovernmentLicenseTypeFilterOptions } from "@/hooks/use-government-license-type-filter-options";
import { DASHBOARD_STATS_TAB_PRELOAD_ALL } from "@/lib/dashboard-query-options";
import { getDashboardLink } from "@/lib/utils";
import { SESSION_KEYS, sessionStorage } from "@/types/session-storage";
import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

export const Route = createFileRoute("/government/question-configuration")({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (search.tab as string) || "questions",
  }),
  component: GovernmentQuestionConfigurationPage,
});

const QUESTION_TABS = [
  { value: "questions", label: "Registration Questions" },
  { value: "deregistration-questions", label: "De-registration Questions" },
  { value: "inspection-questions", label: "Inspection Questions" },
] as const;

export function GovernmentQuestionConfigurationPage() {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const { tab: searchTab } = Route.useSearch();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(searchTab || "questions");

  const [selectedLicenseTypes, setSelectedLicenseTypes] = useState<string[]>(
    () =>
      sessionStorage.get(SESSION_KEYS.GOVERNMENT_SELECTED_LICENSE_TYPES, []),
  );

  const { licenseTypeOptions } = useGovernmentLicenseTypeFilterOptions();
  const { jurisdictionsData } = useGovernmentDashboardStats(
    isAuthSetupComplete,
    [],
    DASHBOARD_STATS_TAB_PRELOAD_ALL,
  );

  const shouldShowLicenseTypeFilter = licenseTypeOptions.length > 1;
  const licenseTypeCatalog = useMemo(
    () =>
      licenseTypeOptions.map((option) => ({
        id: option.value,
        name: option.label,
      })),
    [licenseTypeOptions],
  );

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.GOVERNMENT_SELECTED_LICENSE_TYPES,
      selectedLicenseTypes,
    );
  }, [selectedLicenseTypes]);

  useEffect(() => {
    if (searchTab && searchTab !== selectedTab) {
      setSelectedTab(searchTab);
    }
  }, [searchTab, selectedTab]);

  const handleTabChange = (value: string) => {
    setSelectedTab(value);
    navigate({
      to: location.pathname,
      search: { tab: value },
      replace: true,
    });
  };

  // Government users see only their jurisdiction(s); no jurisdiction filter (like dashboard)
  const effectiveJurisdictions = jurisdictionsData.map((j) => j.id);

  if (!isAuthSetupComplete) {
    return <LoadingScreen message="Authenticating..." />;
  }

  return (
    <div className={SETUP_PAGE_SHELL_CLASS}>
      <Tabs
        value={selectedTab}
        onValueChange={handleTabChange}
        className={SETUP_PAGE_TABS_CLASS}
      >
        <SetupPageTabBar dashboardLink={getDashboardLink(UserType.GOVERNMENT)}>
          <TabsList className={SETUP_TABS_LIST_CLASS}>
            {QUESTION_TABS.map(({ value, label }) => (
              <TabsTrigger
                key={value}
                className={SETUP_TAB_TRIGGER_CLASS}
                value={value}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </SetupPageTabBar>

        <PageHeader
          className={SETUP_PAGE_CHROME_CLASS}
          title="Question Configuration"
          hideBackButton
          subtitle="Registration, de-registration and inspection questions for your jurisdiction"
          action={
            <DashboardFilters
              selectedLicenseTypes={selectedLicenseTypes}
              onSelectedLicenseTypesChange={setSelectedLicenseTypes}
              licenseTypeOptions={licenseTypeOptions}
              showLicenseTypeFilter={shouldShowLicenseTypeFilter}
            />
          }
        />
        <p className="shrink-0 text-muted-foreground text-sm mt-2">
          {
            {
              questions:
                "View and manage registration questions for your jurisdiction.",
              "deregistration-questions":
                "View and manage de-registration questions for your jurisdiction.",
              "inspection-questions":
                "View and manage inspection questions for your jurisdiction.",
            }[selectedTab]
          }
        </p>

        <TabsContent value="questions" className={SETUP_PAGE_LIST_TAB_CLASS}>
          <DashboardQuestions
            isAuthSetupComplete={isAuthSetupComplete}
            selectedJurisdictions={effectiveJurisdictions}
            selectedLicenseTypes={selectedLicenseTypes}
            onSelectedLicenseTypesChange={setSelectedLicenseTypes}
            licenseTypeCatalog={licenseTypeCatalog}
          />
        </TabsContent>

        <TabsContent
          value="deregistration-questions"
          className={SETUP_PAGE_LIST_TAB_CLASS}
        >
          <DashboardDeregistrationQuestions
            isAuthSetupComplete={isAuthSetupComplete}
            selectedJurisdictions={effectiveJurisdictions}
            selectedLicenseTypes={selectedLicenseTypes}
            onSelectedLicenseTypesChange={setSelectedLicenseTypes}
            licenseTypeCatalog={licenseTypeCatalog}
          />
        </TabsContent>

        <TabsContent
          value="inspection-questions"
          className={SETUP_PAGE_LIST_TAB_CLASS}
        >
          <DashboardInspectionQuestions
            isAuthSetupComplete={isAuthSetupComplete}
            selectedJurisdictions={effectiveJurisdictions}
            selectedLicenseTypes={selectedLicenseTypes}
            onSelectedLicenseTypesChange={setSelectedLicenseTypes}
            licenseTypeCatalog={licenseTypeCatalog}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
